import { motion } from 'framer-motion';
import { ArrowRight, ChevronDown, Building2, Handshake, Target, Award } from 'lucide-react';

const Hero = () => {
  return (
    <section className="relative min-h-screen flex flex-col justify-center pt-20 overflow-hidden bg-gradient-to-b from-blue-50 via-white to-blue-50 text-gray-800">
      <div className="absolute inset-0 z-0 overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGZpbGw9IiNmZmZmZmYiIGQ9Ik0wIDBoNjB2NjBIMHoiLz48cGF0aCBkPSJNNjAgMEgwdjYwaDYwVjB6TTMwIDMwaDMwVjBoLTMwdjMwek0wIDMwaDMwVjBoLTMwdjMwek0wIDYwaDMwVjMwaC0zMHYzMHpNMzAgNjBoMzBWMzBoLTMwdjMweiIgc3Ryb2tlPSIjZTBlNWViIiBzdHJva2Utb3BhY2l0eT0iLjUiLz48L2c+PC9zdmc+')] opacity-30"></div>
        <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] rounded-full bg-gradient-to-r from-primary-500/10 to-primary-300/10 blur-3xl animate-pulse-slow"></div>
        <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] rounded-full bg-gradient-to-r from-secondary-400/10 to-secondary-200/10 blur-3xl animate-float-slow"></div>

        <svg className="absolute inset-0 w-full h-full" xmlns="https://www.w3.org/2000/svg">
          <defs>
            <linearGradient id="grad1" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" style={{ stopColor: 'rgb(59, 130, 246)', stopOpacity: 0.05 }} />
              <stop offset="100%" style={{ stopColor: 'rgb(147, 197, 253)', stopOpacity: 0.05 }} />
            </linearGradient>
          </defs>
          <motion.path
            d="M0,100 C150,200 350,0 500,100 L500,00 L0,0 Z"
            fill="url(#grad1)"
            initial={{ opacity: 0 }}
            animate={{
              opacity: 0.5,
              y: [0, 10, 0],
            }}
            transition={{
              duration: 8,
              repeat: Infinity,
              repeatType: 'reverse',
            }}
          />
        </svg>
      </div>

      <div className="container mx-auto px-4 md:px-6 z-10 py-16">
        <div className="text-center max-w-4xl mx-auto mb-16">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center py-2 px-5 rounded-full bg-primary-50 text-primary-600 text-sm font-medium mb-6 border border-primary-100 shadow-sm"
          >
            <Building2 size={16} className="mr-2" />
            AI SaaS & Online Business Acquisitions
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight leading-tight mb-8 text-gray-800"
          >
            Acquiring and Building{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-600 to-primary-500">
              AI-Driven Online Businesses
            </span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="text-xl text-gray-600 mb-10 max-w-3xl mx-auto leading-relaxed"
          >
            ACSE Capital is focused on acquisitions and venture opportunities in AI-related SaaS startups and online
            businesses. We look for digital products, software platforms, and internet-native companies where thoughtful
            ownership can accelerate the next stage of growth.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-6"
          >
            <motion.a
              whileHover={{ scale: 1.05, boxShadow: '0 10px 25px -5px rgba(59, 130, 246, 0.4)' }}
              whileTap={{ scale: 0.95 }}
              href="/get-started"
              className="px-8 py-4 rounded-lg bg-primary-600 text-white font-medium transition-all duration-300 hover:bg-primary-500 shadow-lg shadow-primary-600/20 flex items-center justify-center"
            >
              <span>Get Started</span>
              <ArrowRight size={18} className="ml-2" />
            </motion.a>
            <motion.a
              whileHover={{ scale: 1.05, boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)' }}
              whileTap={{ scale: 0.95 }}
              href="/get-started#acquisition-fit"
              className="px-8 py-4 rounded-lg bg-white text-gray-700 font-medium transition-all duration-300 hover:bg-gray-50 border border-gray-200 shadow-md flex items-center justify-center"
            >
              <span>Acquisition Fit</span>
            </motion.a>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.8 }}
          className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-16"
        >
          {[
            {
              icon: <Handshake className="text-primary-600" size={32} />,
              title: 'Online Business Focus',
              description: 'We focus on SaaS, software, digital products, and internet-native businesses.',
            },
            {
              icon: <Target className="text-primary-600" size={32} />,
              title: 'Acquisition-Led Growth',
              description: 'We pursue acquisitions, founder transitions, and strategic opportunities with clear operating upside.',
            },
            {
              icon: <Award className="text-primary-600" size={32} />,
              title: 'AI Venture Interest',
              description: 'We are especially interested in AI-related SaaS startups and software-enabled online businesses.',
            },
          ].map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.9 + index * 0.1 }}
              className="bg-white shadow-lg border border-gray-100 rounded-xl p-6 hover:shadow-xl transition-all duration-300"
              whileHover={{ y: -5 }}
            >
              <div className="bg-primary-50 w-16 h-16 rounded-lg flex items-center justify-center mb-4">
                {feature.icon}
              </div>
              <h3 className="text-xl font-semibold mb-3 text-gray-800">{feature.title}</h3>
              <p className="text-gray-600">{feature.description}</p>
            </motion.div>
          ))}
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5, duration: 0.8 }}
          className="flex justify-center mt-20"
        >
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 1.5, repeat: Infinity }}
            className="flex flex-col items-center text-gray-500"
          >
            <span className="text-sm mb-2">Scroll to explore</span>
            <ChevronDown size={20} />
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
